import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  IconButton,
  ListItemButton,
  CircularProgress,
  Button,
  Collapse,
  Alert,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ShareIcon from "@mui/icons-material/Share";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { gamoraApi, ChatSession } from "../services/api";
import { workflowService, ComplianceWorkflow } from "../services/workflowService";
import SkeletonLoader from "./SkeletonLoader";
import ShareDialog from "./ShareDialog";
import WorkflowShareDialog from "./WorkflowShareDialog";
import Logo from "./Logo";



// Extend Window interface for caching
declare global {
  interface Window {
    _allChatSessions?: ChatSession[];
    _allWorkflows?: ComplianceWorkflow[];
    _lastRefreshTime?: number;
  }
}

export default function Sidebar({
  onSessionSelect,
  selectedSessionId,
  onNewChat,
  onWorkflowSelect,
  selectedWorkflowId,
  onFormsClick,
  onNewWorkflow,
}: {
  onSessionSelect: (id: string) => void;
  selectedSessionId?: string;
  onNewChat?: () => void;
  onWorkflowSelect?: (id: string) => void;
  selectedWorkflowId?: string;
  onFormsClick?: () => void;
  onNewWorkflow?: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [sessionLoading, setSessionLoading] = useState<string | null>(null);
  const [workflowLoadingId, setWorkflowLoadingId] = useState<string | null>(null);
  const [chatsExpanded, setChatsExpanded] = useState(true);
  const [workflowsExpanded, setWorkflowsExpanded] = useState(true);
  const [formsExpanded, setFormsExpanded] = useState(true);


  // Custom client-side pagination for chat sessions
  const [recentMessages, setRecentMessages] = useState<ChatSession[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatLoadingMore, setChatLoadingMore] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatPage, setChatPage] = useState(1);
  const chatLimit = 10;
  const [chatMenuAnchor, setChatMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [shareSessionId, setShareSessionId] = useState<string | null>(null);
  const [shareSessionName, setShareSessionName] = useState<string>('');
  const [workflowMenuAnchor, setWorkflowMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [deletingWorkflowId, setDeletingWorkflowId] = useState<string | null>(null);
  const [shareWorkflowId, setShareWorkflowId] = useState<string | null>(null);
  const [shareWorkflowName, setShareWorkflowName] = useState<string>('');

  // Handle chat menu open
  const handleChatMenuOpen = (sessionId: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent triggering the ListItemButton click
    setChatMenuAnchor({ ...chatMenuAnchor, [sessionId]: event.currentTarget });
  };

  // Handle chat menu close
  const handleChatMenuClose = (sessionId: string) => {
    setChatMenuAnchor({ ...chatMenuAnchor, [sessionId]: null });
  };

  const handleShareChat = (sessionId: string, sessionName?: string) => {
    setShareSessionId(sessionId);
    setShareSessionName(sessionName || '');
  };

  const handleShareWorkflow = (workflowId: string, workflowName?: string) => {
    setShareWorkflowId(workflowId);
    setShareWorkflowName(workflowName || '');
  };

  // Handle delete chat session
  const handleDeleteChat = async (sessionId: string) => {
    handleChatMenuClose(sessionId);
    setDeletingSessionId(sessionId);
    
    try {
      await gamoraApi.updateChatSessionStatus(sessionId, 'deleted');
      // Remove from local state
      setRecentMessages(prev => prev.filter(msg => msg.session_id !== sessionId));
      // If this was the selected session, clear selection
      if (selectedSessionId === sessionId && onSessionSelect) {
        onSessionSelect('');
      }
    } catch (err: any) {
      console.error('Error deleting chat session:', err);
      alert('Failed to delete chat session. Please try again.');
    } finally {
      setDeletingSessionId(null);
    }
  };

  const handleWorkflowMenuOpen = (workflowId: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setWorkflowMenuAnchor({ ...workflowMenuAnchor, [workflowId]: event.currentTarget });
  };

  const handleWorkflowMenuClose = (workflowId: string) => {
    setWorkflowMenuAnchor({ ...workflowMenuAnchor, [workflowId]: null });
  };

  const handleDeleteWorkflow = async (workflowId: string) => {
    handleWorkflowMenuClose(workflowId);
    setDeletingWorkflowId(workflowId);
    
    try {
      await workflowService.updateWorkflowStatus(workflowId, 'deleted');
      // Remove from local state
      setWorkflows(prev => prev.filter((w: ComplianceWorkflow) => w.id !== workflowId));
      // If this was the selected workflow, clear selection
      if (selectedWorkflowId === workflowId && onWorkflowSelect) {
        onWorkflowSelect('');
      }
    } catch (err: any) {
      console.error('Error deleting workflow:', err);
      alert('Failed to delete workflow. Please try again.');
    } finally {
      setDeletingWorkflowId(null);
    }
  };

  // Load chat sessions
  const loadChatSessions = useCallback(async (page: number = 1, isLoadMore: boolean = false) => {
    if (isLoadMore) {
      setChatLoadingMore(true);
    } else {
      setChatLoading(true);
    }
    setChatError(null);

    try {
      // Use paginated API to fetch only 10 records per page
      const response = await gamoraApi.getChatSessionsPaginated(page, chatLimit);
      const paginatedSessions = Array.isArray(response.sessions) ? response.sessions : [];
      
      if (isLoadMore) {
        setRecentMessages(prev => [...prev, ...paginatedSessions]);
      } else {
        setRecentMessages(paginatedSessions);
      }
      
      setChatHasMore(response.pagination?.has_next || false);
      setChatPage(page);
    } catch (err: any) {
      setChatError(err.message || 'Failed to load chat sessions');
      console.error('Error loading chat sessions:', err);
    } finally {
      setChatLoading(false);
      setChatLoadingMore(false);
    }
  }, [chatLimit]);

  const loadMoreChats = useCallback(() => {
    if (!chatLoadingMore && chatHasMore) {
      loadChatSessions(chatPage + 1, true);
    }
  }, [loadChatSessions, chatPage, chatHasMore, chatLoadingMore]);

  // refreshChats is now called directly in useEffect, keeping for potential future use
  // const refreshChats = useCallback(() => {
  //   setChatPage(1);
  //   loadChatSessions(1, false);
  // }, [loadChatSessions]);

  // Load initial data - only once on mount
  useEffect(() => {
    loadChatSessions(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount


  // Custom client-side pagination for workflows
  const [workflows, setWorkflows] = useState<ComplianceWorkflow[]>([]);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowLoadingMore, setWorkflowLoadingMore] = useState(false);
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [workflowHasMore, setWorkflowHasMore] = useState(false);
  const [workflowPage, setWorkflowPage] = useState(1);
  const workflowLimit = 10;

  // Load workflows
  const loadWorkflows = useCallback(async (page: number = 1, isLoadMore: boolean = false) => {
    if (isLoadMore) {
      setWorkflowLoadingMore(true);
    } else {
      setWorkflowLoading(true);
    }
    setWorkflowError(null);

    try {
      // Use paginated API to fetch only 10 records per page
      const response = await workflowService.getWorkflowsPaginated(page, workflowLimit);
      const paginatedWorkflows = Array.isArray(response.workflows) ? response.workflows : [];
      
      if (isLoadMore) {
        setWorkflows(prev => [...prev, ...paginatedWorkflows]);
      } else {
        setWorkflows(paginatedWorkflows);
      }
      
      setWorkflowHasMore(response.pagination?.has_next || false);
      setWorkflowPage(page);
    } catch (err: any) {
      setWorkflowError(err.message || 'Failed to load workflows');
      console.error('Error loading workflows:', err);
    } finally {
      setWorkflowLoading(false);
      setWorkflowLoadingMore(false);
    }
  }, [workflowLimit]);

  const loadMoreWorkflows = useCallback(() => {
    if (!workflowLoadingMore && workflowHasMore) {
      loadWorkflows(workflowPage + 1, true);
    }
  }, [loadWorkflows, workflowPage, workflowHasMore, workflowLoadingMore]);

  // refreshWorkflows is now called directly in useEffect, keeping for potential future use
  // const refreshWorkflows = useCallback(() => {
  //   setWorkflowPage(1);
  //   loadWorkflows(1, false);
  // }, [loadWorkflows]);

  // Load initial data - only once on mount
  useEffect(() => {
    loadWorkflows(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount


  // Manual refresh functions - no automatic refresh
  const handleRefreshChats = useCallback(() => {
    delete window._allChatSessions;
    setChatPage(1);
    loadChatSessions(1, false);
  }, [loadChatSessions]);

  const handleRefreshWorkflows = useCallback(() => {
    delete window._allWorkflows;
    setWorkflowPage(1);
    loadWorkflows(1, false);
  }, [loadWorkflows]);

  const handleNewChat = async () => {
    try {
      // Don't create a session immediately - just clear the current session
      localStorage.removeItem('gamora_session_id');
      onSessionSelect(''); // Pass empty string to indicate no session
      // Notify parent to clear chat state
      if (onNewChat) {
        onNewChat();
      }
    } catch (error) {
      console.error('Failed to start new chat:', error);
    }
  };

  const handleSessionClick = async (sessionId: string) => {
    setSessionLoading(sessionId);
    try {
      localStorage.setItem('gamora_session_id', sessionId);
      onSessionSelect(sessionId);
    } catch (error) {
      console.error('Failed to select session:', error);
    } finally {
      setSessionLoading(null);
    }
  };

  const handleWorkflowClick = async (workflowId: string) => {
    setWorkflowLoadingId(workflowId);
    try {
      if (onWorkflowSelect) {
        onWorkflowSelect(workflowId);
      }
    } catch (error) {
      console.error('Failed to select workflow:', error);
    } finally {
      setWorkflowLoadingId(null);
    }
  };


  return (
    <>
    <Drawer
      variant="permanent"
      open
      sx={{
        width: collapsed ? 64 : 280,
        flexShrink: 0,
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        '& .MuiDrawer-paper': {
          width: collapsed ? 64 : 280,
          boxSizing: 'border-box',
          borderRight: '1px solid #eceef1',
          bgcolor: '#fff',
          color: '#374151',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: 4,
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.08)',
            borderRadius: 100,
          },
          'scrollbar-width': 'thin',
          'scrollbar-color': 'rgba(0,0,0,0.08) transparent',
        },
      }}
    >
      {/* Top section: Logo left, toggle right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 2,
          pt: 2,
          pb: 2,
          minHeight: 56,
        }}
      >
        {collapsed ? (
          <Tooltip title="Expand sidebar" placement="right" arrow>
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: '#111827',
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& img': { filter: 'brightness(0) invert(1)' },
                '&:hover': { bgcolor: '#1f2937' },
              }}
            >
              <Logo size={20} />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#111827',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& img': { filter: 'brightness(0) invert(1)' },
            }}>
              <Logo size={24} />
            </Box>
            <Tooltip title="Collapse sidebar" placement="right" arrow>
              <IconButton
                onClick={() => setCollapsed(!collapsed)}
                sx={{
                  bgcolor: "transparent",
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  color: '#9ca3af',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: '#f3f4f6',
                    color: '#374151',
                  },
                }}
              >
                <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Nav actions */}
      <Box sx={{ px: collapsed ? 0 : 1, mt: 0.5, mb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {collapsed ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title="New chat" placement="right" arrow>
              <IconButton
                onClick={handleNewChat}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  color: '#6b7280',
                  '&:hover': { bgcolor: '#f3f4f6', color: '#111827' },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <ListItemButton
            onClick={handleNewChat}
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: '8px',
              gap: 1.25,
              '&:hover': { bgcolor: '#f3f4f6' },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 17, color: '#6b7280' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#374151' }}>
              New chat
            </Typography>
          </ListItemButton>
        )}
      </Box>

      {/* Divider */}
      {!collapsed && (
        <Box sx={{ mx: 2, mb: 1, borderBottom: '1px solid #eceef1' }} />
      )}

      {/* Chats Section */}
      <Box sx={{ mt: 0.25 }}>
        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
            <Tooltip title="Chats" placement="right" arrow>
              <IconButton
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  color: '#9ca3af',
                  '&:hover': { bgcolor: '#f3f4f6', color: '#374151' },
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, mb: 0.25 }}>
            <ListItemButton
              onClick={() => setChatsExpanded(!chatsExpanded)}
              sx={{
                px: 1,
                py: 0.4,
                borderRadius: '6px',
                flexGrow: 1,
                '&:hover': {
                  bgcolor: '#f3f4f6',
                },
              }}
            >
              <Typography
                sx={{ color: "#9ca3af", fontWeight: 600, fontSize: '0.7rem', flexGrow: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Chats
              </Typography>
              {chatsExpanded ? <ExpandLessIcon sx={{ fontSize: 15, color: '#c4c9d0' }} /> : <ExpandMoreIcon sx={{ fontSize: 15, color: '#c4c9d0' }} />}
            </ListItemButton>
            <IconButton
              onClick={handleRefreshChats}
              size="small"
              sx={{
                ml: 0.25,
                p: 0.4,
                color: '#c4c9d0',
                '&:hover': {
                  bgcolor: '#f3f4f6',
                  color: '#374151',
                },
              }}
              title="Refresh chats"
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}
        
        <Collapse in={!collapsed && chatsExpanded} timeout="auto" unmountOnExit>
          {chatLoading ? (
            <Box sx={{ px: 2, py: 1 }}>
              <SkeletonLoader type="chat" count={3} />
            </Box>
          ) : chatError ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity="error" sx={{ fontSize: '12px', py: 0.5 }}>
                {chatError}
              </Alert>
            </Box>
          ) : (
            <List sx={{ py: 0, px: 1, mt: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {recentMessages.length === 0 && (
                <ListItem disablePadding>
                  <ListItemText sx={{ color: '#9ca3af', fontSize: '0.78rem', pl: 0.5, my: 0 }} primary="No recent chats" />
                </ListItem>
              )}
              {Array.isArray(recentMessages) && recentMessages.map((msg: ChatSession, idx) => (
                <ListItem 
                  key={msg.session_id || idx} 
                  disablePadding
                  sx={{
                    '&:hover .chat-menu-button, &:hover .chat-share-button': {
                      opacity: 1,
                    },
                  }}
                >
                  <ListItemButton
                    sx={{
                      py: '7px',
                      px: 1.25,
                      minHeight: 0,
                      borderRadius: '8px',
                      position: 'relative',
                      '&:hover .chat-actions': { opacity: 1, pointerEvents: 'auto' },
                      '&:hover .chat-title': { pr: 6.5 },
                      '&.Mui-selected': {
                        bgcolor: '#f3f4f6',
                        '&:hover': {
                          bgcolor: '#f3f4f6',
                        },
                      },
                      '&:hover': {
                        bgcolor: '#f8f9fb',
                      },
                    }}
                    onClick={() => handleSessionClick(msg.session_id)}
                    selected={selectedSessionId === msg.session_id}
                    disabled={sessionLoading === msg.session_id || deletingSessionId === msg.session_id}
                  >
                    {sessionLoading === msg.session_id || deletingSessionId === msg.session_id ? (
                      <CircularProgress size={14} sx={{ mr: 1 }} />
                    ) : (
                      <>
                        <ListItemText
                          primary={
                            msg.name 
                              ? msg.name
                              : `Chat ${msg.session_id?.slice(0, 8) || 'Session'}`
                          }
                          primaryTypographyProps={{ 
                            fontSize: '0.8rem', 
                            lineHeight: 1.4,
                            color: selectedSessionId === msg.session_id ? "#111827" : "#4b5563",
                            fontWeight: selectedSessionId === msg.session_id ? 500 : 400,
                            noWrap: true,
                            title: msg.name || '',
                            className: 'chat-title',
                            sx: {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              transition: 'padding-right 0.2s ease',
                            },
                          }}
                          sx={{ flex: 1, minWidth: 0, my: 0 }}
                        />
                        <Box
                          className="chat-actions"
                          sx={{
                            position: 'absolute',
                            right: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: 0.25,
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 0.2s ease',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareChat(msg.session_id, msg.name);
                            }}
                            sx={{ p: 0.5, '&:hover': { bgcolor: '#e5e7eb' } }}
                          >
                            <ShareIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleChatMenuOpen(msg.session_id, e)}
                            sx={{ p: 0.5, '&:hover': { bgcolor: '#e5e7eb' } }}
                          >
                            <MoreVertIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                          </IconButton>
                        </Box>
                      </>
                    )}
                  </ListItemButton>
                  <Menu
                    anchorEl={chatMenuAnchor[msg.session_id]}
                    open={!!chatMenuAnchor[msg.session_id]}
                    onClose={() => handleChatMenuClose(msg.session_id)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      sx: {
                        borderRadius: 1.5,
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
                        minWidth: 140,
                        p: 0.25,
                      },
                    }}
                  >
                    <MenuItem 
                      onClick={() => handleDeleteChat(msg.session_id)}
                      disabled={deletingSessionId === msg.session_id}
                      sx={{
                        fontSize: '0.85rem',
                        py: 0.75,
                        px: 1.5,
                        minHeight: 36,
                      }}
                    >
                      <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
                      Delete
                    </MenuItem>
                  </Menu>
                </ListItem>
              ))}
              
              {/* Load More Button for Chats */}
              {chatHasMore && (
                <ListItem disablePadding>
                  <Button
                    onClick={() => {
                      console.log('🔍 Sidebar: Load More Chats clicked');
                      loadMoreChats();
                    }}
                    disabled={chatLoadingMore}
                    sx={{
                      width: '100%',
                      py: 0.5,
                      fontSize: '11px',
                      textTransform: 'none',
                      color: '#9ca3af',
                      '&:hover': {
                        bgcolor: '#f3f4f6',
                      },
                    }}
                  >
                    {chatLoadingMore ? (
                      <CircularProgress size={12} sx={{ mr: 1 }} />
                    ) : (
                      <RefreshIcon sx={{ fontSize: 12, mr: 0.5 }} />
                    )}
                    {chatLoadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </ListItem>
              )}
            </List>
          )}
        </Collapse>
      </Box>

      {/* Workflows Section */}
      <Box sx={{ mt: 0.75 }}>
        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
            <Tooltip title="Workflows" placement="right" arrow>
              <IconButton
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  color: '#9ca3af',
                  '&:hover': { bgcolor: '#f3f4f6', color: '#374151' },
                }}
              >
                <FolderOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, mb: 0.25 }}>
            <ListItemButton
              onClick={() => setWorkflowsExpanded(!workflowsExpanded)}
              sx={{
                flex: 1,
                px: 1,
                py: 0.4,
                borderRadius: '6px',
                '&:hover': {
                  bgcolor: '#f3f4f6',
                },
              }}
            >
              <Typography
                sx={{ color: "#9ca3af", fontWeight: 600, fontSize: '0.7rem', flexGrow: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Workflows
              </Typography>
              {workflowsExpanded ? <ExpandLessIcon sx={{ fontSize: 15, color: '#c4c9d0' }} /> : <ExpandMoreIcon sx={{ fontSize: 15, color: '#c4c9d0' }} />}
            </ListItemButton>
            <IconButton
              onClick={handleRefreshWorkflows}
              size="small"
              sx={{
                ml: 0.25,
                p: 0.4,
                color: '#c4c9d0',
                '&:hover': {
                  bgcolor: '#f3f4f6',
                  color: '#374151',
                },
              }}
              title="Refresh workflows"
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton
              onClick={() => {
                if (onNewWorkflow) {
                  onNewWorkflow();
                } else {
                  console.log('Create new workflow clicked - no handler provided');
                }
              }}
              sx={{
                ml: 0.25,
                p: 0.4,
                color: '#c4c9d0',
                '&:hover': {
                  bgcolor: '#f3f4f6',
                  color: '#374151',
                },
              }}
              size="small"
              title="Create New Workflow"
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}
        
        <Collapse in={!collapsed && workflowsExpanded} timeout="auto" unmountOnExit>
          {workflowLoading ? (
            <Box sx={{ px: 2, py: 1 }}>
              <SkeletonLoader type="workflow" count={3} />
            </Box>
          ) : workflowError ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity="error" sx={{ fontSize: '12px', py: 0.5 }}>
                {workflowError}
              </Alert>
            </Box>
          ) : (
            <List sx={{ py: 0, px: 1, mt: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {workflows.length === 0 && (
                <ListItem disablePadding>
                  <ListItemText sx={{ color: '#9ca3af', fontSize: '0.78rem', pl: 0.5, my: 0 }} primary="No workflows" />
                </ListItem>
              )}
              {Array.isArray(workflows) && workflows.map((workflow: ComplianceWorkflow, idx) => (
                <ListItem 
                  key={workflow.id || idx} 
                  disablePadding
                  sx={{
                    '&:hover .workflow-menu-button': {
                      opacity: 1,
                    },
                  }}
                >
                  <ListItemButton
                    sx={{
                      py: '6px',
                      px: 1.25,
                      minHeight: 0,
                      borderRadius: '8px',
                      position: 'relative',
                      '&:hover .workflow-actions': { opacity: 1, pointerEvents: 'auto' },
                      '&:hover .workflow-title': { pr: 6.5 },
                      '&.Mui-selected': {
                        bgcolor: '#f3f4f6',
                        '&:hover': {
                          bgcolor: '#f3f4f6',
                        },
                      },
                      '&:hover': {
                        bgcolor: '#f8f9fb',
                      },
                    }}
                    onClick={() => handleWorkflowClick(workflow.id)}
                    selected={selectedWorkflowId === workflow.id}
                    disabled={workflowLoadingId === workflow.id || deletingWorkflowId === workflow.id}
                  >
                    {workflowLoadingId === workflow.id || deletingWorkflowId === workflow.id ? (
                      <CircularProgress size={14} sx={{ mr: 1 }} />
                    ) : (
                      <>
                        <ListItemText
                          primary={
                            workflow.name 
                              ? workflow.name
                              : `Workflow ${workflow.id?.slice(0, 8) || 'Unknown'}`
                          }
                          secondary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.15 }}>
                              <Typography component="span" variant="caption" sx={{ color: '#9ca3af', fontSize: '0.66rem' }}>
                                {workflow.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                              </Typography>
                              <Typography component="span" variant="caption" sx={{ color: '#c4c9d0', fontSize: '0.66rem' }}>
                                {workflow.completion_percentage?.toFixed(0) || 0}%
                              </Typography>
                            </Box>
                          }
                          primaryTypographyProps={{ 
                            fontSize: '0.8rem',
                            lineHeight: 1.3,
                            color: selectedWorkflowId === workflow.id ? "#111827" : "#4b5563",
                            fontWeight: selectedWorkflowId === workflow.id ? 500 : 400,
                            noWrap: true,
                            title: workflow.name || '',
                            className: 'workflow-title',
                            sx: {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              transition: 'padding-right 0.2s ease',
                            },
                          }}
                          sx={{ flex: 1, minWidth: 0, my: '2px' }}
                        />
                        <Box
                          className="workflow-actions"
                          sx={{
                            position: 'absolute',
                            right: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            gap: 0.25,
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 0.2s ease',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareWorkflow(workflow.id, workflow.name);
                            }}
                            sx={{ p: 0.5, '&:hover': { bgcolor: '#e5e7eb' } }}
                          >
                            <ShareIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleWorkflowMenuOpen(workflow.id, e)}
                            sx={{ p: 0.5, '&:hover': { bgcolor: '#e5e7eb' } }}
                          >
                            <MoreVertIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                          </IconButton>
                        </Box>
                      </>
                    )}
                  </ListItemButton>
                  <Menu
                    anchorEl={workflowMenuAnchor[workflow.id]}
                    open={!!workflowMenuAnchor[workflow.id]}
                    onClose={() => handleWorkflowMenuClose(workflow.id)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem 
                      onClick={() => handleDeleteWorkflow(workflow.id)}
                      disabled={deletingWorkflowId === workflow.id}
                    >
                      <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
                      Delete
                    </MenuItem>
                  </Menu>
                </ListItem>
              ))}
              
              {/* Load More Button for Workflows */}
              {workflowHasMore && (
                <ListItem disablePadding>
                  <Button
                    onClick={() => {
                      console.log('🔍 Sidebar: Load More Workflows clicked');
                      loadMoreWorkflows();
                    }}
                    disabled={workflowLoadingMore}
                    sx={{
                      width: '100%',
                      py: 0.5,
                      fontSize: '11px',
                      textTransform: 'none',
                      color: '#9ca3af',
                      '&:hover': {
                        bgcolor: '#f3f4f6',
                      },
                    }}
                  >
                    {workflowLoadingMore ? (
                      <CircularProgress size={12} sx={{ mr: 1 }} />
                    ) : (
                      <RefreshIcon sx={{ fontSize: 12, mr: 0.5 }} />
                    )}
                    {workflowLoadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </ListItem>
              )}
            </List>
          )}
        </Collapse>
      </Box>

      {/* Forms Section */}
      <Box sx={{ mt: 0.75 }}>
        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
            <Tooltip title="Forms & Templates" placement="right" arrow>
              <IconButton
                onClick={() => onFormsClick?.()}
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  color: '#9ca3af',
                  '&:hover': { bgcolor: '#f3f4f6', color: '#374151' },
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {!collapsed && (
          <Box sx={{ px: 1.5, mb: 0.25 }}>
            <ListItemButton
              onClick={() => setFormsExpanded(!formsExpanded)}
              sx={{
                px: 1,
                py: 0.4,
                borderRadius: '6px',
                '&:hover': {
                  bgcolor: '#f3f4f6',
                },
              }}
            >
              <Typography
                sx={{ color: "#9ca3af", fontWeight: 600, fontSize: '0.7rem', flexGrow: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Forms & Templates
              </Typography>
              {formsExpanded ? <ExpandLessIcon sx={{ fontSize: 15, color: '#c4c9d0' }} /> : <ExpandMoreIcon sx={{ fontSize: 15, color: '#c4c9d0' }} />}
            </ListItemButton>
          </Box>
        )}
        
        <Collapse in={!collapsed && formsExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#9ca3af', fontSize: '0.78rem' }}>
              Download compliance forms and templates
            </Typography>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => {
                if (onFormsClick) {
                  onFormsClick();
                }
              }}
              sx={{
                color: '#374151',
                borderColor: '#d1d5db',
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '0.8rem',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f3f4f6',
                },
              }}
            >
              Browse Forms
            </Button>
          </Box>
        </Collapse>
      </Box>

   
    </Drawer>
    <ShareDialog
      open={Boolean(shareSessionId)}
      onClose={() => setShareSessionId(null)}
      sessionId={shareSessionId || ''}
      sessionName={shareSessionName}
    />
    <WorkflowShareDialog
      open={Boolean(shareWorkflowId)}
      onClose={() => setShareWorkflowId(null)}
      workflowId={shareWorkflowId || ''}
      workflowName={shareWorkflowName}
    />
    </>
  );
}
